/**
 * GalleryLightbox — click-to-zoom fullscreen viewer for vendor gallery
 * photos. Supports keyboard nav (← → Esc) and mouse click on prev/next.
 *
 * The images are still rendered by the parent (FramedImage grid). This
 * component only owns the modal. It accepts the flat list of image URLs
 * plus the currently opened index (or -1 to be closed).
 *
 * Each image tile in VendorProfile also gets an optional "Report" button
 * (see VendorProfile.jsx) so a member can flag adult / inappropriate
 * content for admin review with a single tap.
 */
import React, { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Flag, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function GalleryLightbox({
  images = [],
  index,               // number | -1 closed
  onClose,
  onIndex,             // (nextIndex) => void
  onReport,            // (url, index) => void  — optional
}) {
  const open = index >= 0 && index < images.length;
  const src = open ? images[index] : null;

  const prev = useCallback(() => {
    if (!open) return;
    onIndex((index - 1 + images.length) % images.length);
  }, [open, index, images.length, onIndex]);

  const next = useCallback(() => {
    if (!open) return;
    onIndex((index + 1) % images.length);
  }, [open, index, images.length, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, prev, next, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Gallery image viewer"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="gallery-lightbox-close"
        aria-label="Close viewer"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Counter + action toolbar */}
      <div
        className="absolute top-4 left-4 right-20 flex items-center gap-2 text-white/90"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-xs font-mono bg-white/10 rounded-full px-3 py-1">
          {index + 1} / {images.length}
        </span>
        <div className="flex-1" />
        {onReport && (
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/10 rounded-full"
            onClick={() => onReport(src, index)}
            data-testid="gallery-lightbox-report"
          >
            <Flag className="h-4 w-4 mr-1" /> Report this image
          </Button>
        )}
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 transition-colors"
          onClick={(e) => e.stopPropagation()}
          data-testid="gallery-lightbox-open-tab"
        >
          <Download className="h-3.5 w-3.5" /> Open original
        </a>
      </div>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="gallery-lightbox-prev"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="gallery-lightbox-next"
            aria-label="Next image"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Image — click on image itself should NOT close the modal */}
      <img
        src={src}
        alt={`Gallery image ${index + 1}`}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="gallery-lightbox-image"
      />
    </div>
  );
}
